/**
 * Entry parameters of the payment app.
 *
 * The mobile app links here with `?refreshtoken=…&returnUrl=…`. Both values are
 * read ONCE at module load - before React renders and before the router reads
 * the URL - so that navigating to another route can never lose them.
 *
 * The refresh token is stripped from the address bar right away: it must not
 * survive in the browser history. `returnUrl` and `lang` stay in the URL and are
 * carried along by every in-app navigation, so that a reload on a deep link
 * still returns to the right origin and language.
 */
const params = new URLSearchParams(window.location.search);

export const entryRefreshToken: string | null = params.get("refreshtoken");
export const entryReturnUrl: string | null = params.get("returnUrl");

if (entryRefreshToken) {
  params.delete("refreshtoken");
  const remaining = params.toString();
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + (remaining ? `?${remaining}` : ""),
  );
}

/** Query string kept on every in-app navigation (returnUrl, lang). */
export const entryQuery: string = (() => {
  const kept = new URLSearchParams();
  if (entryReturnUrl) {
    kept.set("returnUrl", entryReturnUrl);
  }
  const lang = params.get("lang");
  if (lang) {
    kept.set("lang", lang);
  }
  const search = kept.toString();
  return search ? `?${search}` : "";
})();

/** Appends the kept entry parameters to an in-app path. */
export const withEntryQuery = (path: string): string => `${path}${entryQuery}`;

/** Origin Stripe returns to after the checkout. */
export const checkoutOriginUrl = (): string =>
  entryReturnUrl || window.location.origin;
